<script>
import { GlDropdown, GlDropdownDivider, GlDropdownItem, GlFormGroup } from '@gitlab/ui';
import * as Sentry from '@sentry/browser';
import { ValidationProvider } from 'vee-validate';
import ZuoraNewCreditCardForm from '~/javascripts/purchase/components/zuora_new_credit_card_form.vue';
import getPaymentMethodsQuery from '~/javascripts/purchase/shared/graphql/queries/get_payment_methods.query.graphql';
import { extractActiveModelError } from '~/javascripts/lib/error_utils';
import validationStateMixin from '~/javascripts/forms/validation/mixins/validation_state_mixin';
import { NEW_CREDIT_CARD_VALUE } from '../../constants';

const NEW_CREDIT_CARD_TEXT = 'Add new Credit Card';

export default {
  name: 'PaymentMethodsSelect',
  NEW_CREDIT_CARD_VALUE,
  NEW_CREDIT_CARD_TEXT,
  components: {
    GlDropdown,
    GlDropdownDivider,
    GlDropdownItem,
    GlFormGroup,
    ZuoraNewCreditCardForm,
    ValidationProvider,
  },
  mixins: [validationStateMixin],
  inject: {
    zuoraAccountNumber: {
      default: null,
    },
  },
  props: {
    initialPaymentMethodId: {
      type: String,
      required: false,
      default: null,
    },
    enableNewCreditCard: {
      type: Boolean,
      required: false,
      default: true,
    },
    validationEnabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    label: {
      type: String,
      required: false,
      default: 'Payment method',
    },
    paymentFormClasses: {
      type: [Array, Object, String],
      required: false,
      default: 'gl-grow gl-min-w-31 gl-bg-blue-50',
    },
  },
  data() {
    return {
      paymentMethods: [],
      selectedPaymentMethodId: this.initialPaymentMethodId,
    };
  },
  computed: {
    creditCardOptions() {
      return this.paymentMethods.map((paymentMethod) => {
        const lastFour = paymentMethod.creditCardLastFourDigits;

        return {
          id: paymentMethod.id,
          text: `${paymentMethod.creditCardType} ending ${lastFour}`,
          type: 'cc',
        };
      });
    },
    selectedPaymentMethod() {
      return this.creditCardOptions.find(
        (paymentMethod) => paymentMethod.id === this.selectedPaymentMethodId,
      );
    },
    title() {
      if (this.selectedPaymentMethodId) {
        if (this.selectedPaymentMethodId === NEW_CREDIT_CARD_VALUE) return NEW_CREDIT_CARD_TEXT;
      }
      return this.selectedPaymentMethod?.text || 'Please select';
    },
    validationRules() {
      return this.validationEnabled ? { required: true } : {};
    },
    hasPaymentMethods() {
      return Boolean(this.paymentMethods.length);
    },
    isLoading() {
      return this.$apollo.queries.paymentMethods?.loading;
    },
    showZuoraIframe() {
      return !this.showCreditCardSelect || this.selectedPaymentMethodId === NEW_CREDIT_CARD_VALUE;
    },
    showCreditCardSelect() {
      return this.zuoraAccountNumber && (this.isLoading || this.hasPaymentMethods);
    },
    showPay() {
      return !window.gon.global?.jh;
    },
  },
  mounted() {
    if (!this.zuoraAccountNumber) {
      if (this.showPay) {
        this.setPaymentMethodId(NEW_CREDIT_CARD_VALUE);
      } else {
        this.setPaymentMethodId('NOPAY'); // ON JH
      }
    }
  },
  methods: {
    handleError(error) {
      this.$emit('error', error);
      this.reportError(error);
    },
    reportError(error) {
      Sentry.captureException(error, {
        tags: {
          vue_component: this.$options.name,
        },
      });
    },
    setPaymentMethodId(paymentMethodId) {
      this.selectedPaymentMethodId = paymentMethodId;
      this.$emit('click', this.selectedPaymentMethodId);
    },
    getDropdownVariant(validationContext) {
      const state = this.getValidationState(validationContext);
      return state === false ? 'danger' : 'default';
    },
    async refetchPaymentMethods() {
      try {
        await this.$apollo.queries.paymentMethods?.refetch();
      } catch (error) {
        const activeModelError = extractActiveModelError(error);
        this.handleError(activeModelError);
      }
    },
  },
  apollo: {
    paymentMethods: {
      query: getPaymentMethodsQuery,
      variables() {
        return {
          zuoraAccountNumber: this.zuoraAccountNumber,
        };
      },
      skip() {
        if (window.gon.global?.jh) {
          return true;
        }
        return !this.zuoraAccountNumber;
      },
      update(data) {
        return data.billingAccount?.creditCards || [];
      },
      result({ data }) {
        if (!data.billingAccount?.creditCards?.length) {
          this.setPaymentMethodId(NEW_CREDIT_CARD_VALUE);
        }
      },
      error(error) {
        const activeModelError = extractActiveModelError(error);
        this.handleError(activeModelError);
      },
    },
  },
};
</script>

<template>
  <div v-if="showPay">
    <validation-provider
      v-if="showCreditCardSelect"
      #default="validationContext"
      name="Payment method"
      :rules="validationRules"
      slim
    >
      <gl-form-group
        :label="label"
        :invalid-feedback="validationContext.errors[0]"
        :state="getValidationState(validationContext)"
      >
        <gl-dropdown
          v-model="selectedPaymentMethodId"
          class="gl-w-full"
          role="combobox"
          :text="title"
          data-testid="payment-methods"
          :variant="getDropdownVariant(validationContext)"
          :loading="isLoading"
        >
          <gl-dropdown-item
            v-for="option in creditCardOptions"
            :key="option.id"
            :active="option.id === selectedPaymentMethodId"
            @click="setPaymentMethodId(option.id)"
            >{{ option.text }}</gl-dropdown-item
          >
          <gl-dropdown-divider v-if="creditCardOptions.length > 0" />
          <gl-dropdown-item
            v-if="enableNewCreditCard"
            :key="$options.NEW_CREDIT_CARD_VALUE"
            @click="setPaymentMethodId($options.NEW_CREDIT_CARD_VALUE)"
            >{{ $options.NEW_CREDIT_CARD_TEXT }}</gl-dropdown-item
          >
        </gl-dropdown>
      </gl-form-group>
    </validation-provider>
    <zuora-new-credit-card-form
      v-if="showZuoraIframe"
      :payment-form-classes="paymentFormClasses"
      @success="refetchPaymentMethods"
    />
  </div>
</template>
